import { Maximize2, Minus, X } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useJanelaNaFrente } from '@core/layout/camadas'
import { BotaoDestacar } from '@core/layout/janela-destacada/JanelaDestacada'
import {
  gravarJanela,
  lerJanela,
  limitarPosicao,
  limitarTamanho,
  posicaoPorOmissao,
  redimensionar,
  type Canto,
  type Ponto,
  type Tamanho,
} from './geometriaDaJanelaNo3D'

/** Movimento, em px, abaixo do qual largar a barra minimizada conta
 *  como clique (restaura) e não como arrasto. */
const CLIQUE_PX = 4

export interface JanelaNo3DProps {
  titulo: string
  icone: ReactNode
  /** Chave da memória (posição, tamanho, minimizado) no localStorage. */
  chave: string
  tamanhoPorOmissao: Tamanho
  tamanhoMinimo: Tamanho
  /** Onde abre da primeira vez. */
  canto?: Canto
  /** Identidade na ordem das janelas do 3D (camadas.ts). */
  camada: string
  /** Prefixo dos data-testid: `<prefixo>-janela`, `-titulo`,
   *  `-redimensionar`. */
  prefixo: string
  /** Como se chama o painel nos rótulos ("o painel dos pins"). */
  oQue: string
  /** O que vai na barra do título, depois do nome (contagens, etiquetas). */
  naBarra?: ReactNode
  /** O que vai na barra minimizada, depois do nome. */
  naBarraMinimizada?: ReactNode
  /** Sem isto não há botão de destacar. */
  aoDestacar?: () => void
  aoFechar: () => void
  /** O que acontece ao minimizar (ex.: "os pins continuam como estão"). */
  dicaMinimizar?: string
  children: ReactNode
}

/** Uma janela dentro da área do 3D: arrasta-se pela barra do título sem
 *  sair da área, muda de tamanho pela pega do canto inferior direito,
 *  minimiza-se para uma barra e pode destacar-se para uma janela do
 *  browser (quem a usa decide o que acontece em `aoDestacar`, com
 *  `JanelaDestacada`). Posição, tamanho e minimizado ficam por browser.
 *
 *  Nasceu no painel dos pins BCF; o Mapa usa a mesma. A geometria pura
 *  está em `geometriaDaJanelaNo3D.ts`. */
export default function JanelaNo3D({
  titulo,
  icone,
  chave,
  tamanhoPorOmissao,
  tamanhoMinimo,
  canto = 'inferior-esquerdo',
  camada,
  prefixo,
  oQue,
  naBarra,
  naBarraMinimizada,
  aoDestacar,
  aoFechar,
  dicaMinimizar,
  children,
}: JanelaNo3DProps) {
  const { z, trazer } = useJanelaNaFrente(camada, 'canvas')

  const inicial = useMemo(() => lerJanela(chave), [chave])
  const [pos, setPos] = useState<Ponto | null>(inicial.pos)
  const [escolhido, setEscolhido] = useState<Tamanho | null>(inicial.tamanho)
  const [minimizado, setMinimizado] = useState(inicial.minimizado)
  const [area, setArea] = useState<Tamanho | null>(null)

  const janelaRef = useRef<HTMLElement | null>(null)
  const posRef = useRef(pos)
  const escolhidoRef = useRef(escolhido)
  useEffect(() => {
    posRef.current = pos
    escolhidoRef.current = escolhido
  })

  const tamanho = useMemo(
    () => limitarTamanho(escolhido ?? tamanhoPorOmissao, area ?? { w: Infinity, h: Infinity }, tamanhoMinimo),
    [area, escolhido, tamanhoPorOmissao, tamanhoMinimo],
  )
  const tamanhoRef = useRef(tamanho)
  const areaRef = useRef(area)
  useEffect(() => {
    tamanhoRef.current = tamanho
    areaRef.current = area
  })

  /** A área do 3D e o tamanho da janela (o da barra, minimizada). */
  const medir = useCallback((): { area: Tamanho; janela: Tamanho } | null => {
    const el = janelaRef.current
    const cont = el?.offsetParent ?? el?.parentElement
    if (!el || !cont) return null
    const a = cont.getBoundingClientRect()
    const areaMedida = { w: a.width, h: a.height }
    let janela: Tamanho
    if (el.dataset.minimizada === 'true') {
      const j = el.getBoundingClientRect()
      janela = { w: j.width, h: j.height }
    } else {
      janela = limitarTamanho(escolhidoRef.current ?? tamanhoPorOmissao, areaMedida, tamanhoMinimo)
    }
    return { area: areaMedida, janela }
  }, [tamanhoPorOmissao, tamanhoMinimo])

  // Primeira abertura sem posição guardada: o canto pedido. Com posição
  // guardada (ou ao minimizar/restaurar, que muda o tamanho), volta a
  // caber na área.
  useLayoutEffect(() => {
    const m = medir()
    if (!m) return
    const a = areaRef.current
    if (!a || a.w !== m.area.w || a.h !== m.area.h) setArea(m.area)
    const alvo = posRef.current ?? posicaoPorOmissao(m.area, m.janela, canto)
    const l = limitarPosicao(alvo, m.janela, m.area)
    if (!posRef.current || l.x !== posRef.current.x || l.y !== posRef.current.y) setPos(l)
  }, [minimizado, medir, canto])

  // A área do 3D muda de tamanho (janela do browser, painéis laterais):
  // a janela volta a ficar lá dentro.
  useEffect(() => {
    const cont = janelaRef.current?.offsetParent ?? janelaRef.current?.parentElement
    const reenquadrar = () => {
      const m = medir()
      if (!m) return
      setArea((a) => (a && a.w === m.area.w && a.h === m.area.h ? a : m.area))
      const p = posRef.current
      if (!p) return
      const l = limitarPosicao(p, m.janela, m.area)
      if (l.x !== p.x || l.y !== p.y) setPos(l)
    }
    window.addEventListener('resize', reenquadrar)
    let ro: ResizeObserver | null = null
    if (cont && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(reenquadrar)
      ro.observe(cont)
    }
    return () => {
      window.removeEventListener('resize', reenquadrar)
      ro?.disconnect()
    }
  }, [medir])

  // Memória por browser: posição, tamanho escolhido e minimizado.
  useEffect(() => {
    gravarJanela(chave, { pos, tamanho: escolhido, minimizado })
  }, [chave, pos, escolhido, minimizado])

  const gesto = useRef<
    | { tipo: 'mover'; inicio: Ponto; pos0: Ponto; moveu: boolean; aoLargarSemMover?: () => void }
    | { tipo: 'tamanho'; inicio: Ponto; t0: Tamanho }
    | null
  >(null)

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const g = gesto.current
      if (!g) return
      const dx = e.clientX - g.inicio.x
      const dy = e.clientY - g.inicio.y
      const m = medir()
      if (g.tipo === 'tamanho') {
        const p = posRef.current ?? { x: 0, y: 0 }
        setEscolhido(
          m ? redimensionar(g.t0, { x: dx, y: dy }, p, m.area, tamanhoMinimo) : { w: g.t0.w + dx, h: g.t0.h + dy },
        )
        return
      }
      if (!g.moveu && Math.hypot(dx, dy) < CLIQUE_PX) return
      g.moveu = true
      const alvo = { x: g.pos0.x + dx, y: g.pos0.y + dy }
      setPos(m ? limitarPosicao(alvo, m.janela, m.area) : alvo)
    }
    const onUp = () => {
      const g = gesto.current
      gesto.current = null
      if (g && g.tipo === 'mover' && !g.moveu) g.aoLargarSemMover?.()
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [medir, tamanhoMinimo])

  const comecarArrasto = (e: React.PointerEvent, aoLargarSemMover?: () => void) => {
    if (e.button !== 0) return
    // Os botões e campos da barra fazem a sua acção; não arrastam.
    if ((e.target as Element).closest?.('button,input,select,a')) return
    e.preventDefault()
    gesto.current = {
      tipo: 'mover',
      inicio: { x: e.clientX, y: e.clientY },
      pos0: posRef.current ?? { x: 0, y: 0 },
      moveu: false,
      aoLargarSemMover,
    }
  }

  const comecarTamanho = (e: React.PointerEvent) => {
    if (e.button !== 0) return
    e.preventDefault()
    e.stopPropagation()
    gesto.current = { tipo: 'tamanho', inicio: { x: e.clientX, y: e.clientY }, t0: tamanhoRef.current }
  }

  const posicao = pos
    ? { left: pos.x, top: pos.y }
    : // Antes da primeira medição: invisível no canto, para medir sem
      // piscar por cima do 3D.
      { left: 0, top: 0, visibility: 'hidden' as const }

  const botaoBarra = 'rounded p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]'

  if (minimizado) {
    return (
      <div
        ref={(el) => {
          janelaRef.current = el
        }}
        role="dialog"
        aria-label={`${titulo} (minimizado)`}
        data-testid={`${prefixo}-janela`}
        data-minimizada="true"
        style={{ ...posicao, zIndex: z }}
        onPointerDownCapture={trazer}
        onPointerDown={(e) => comecarArrasto(e, () => setMinimizado(false))}
        title="Arrasta para mudar de sítio; clica para restaurar"
        className="pointer-events-auto absolute flex cursor-move select-none items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-panel)] px-2 py-1 text-xs shadow-lg"
      >
        {icone}
        <span className="font-medium text-[var(--color-text)]">{titulo}</span>
        {naBarraMinimizada}
        <button
          type="button"
          onClick={() => setMinimizado(false)}
          aria-label={`Restaurar ${oQue}`}
          title="Restaurar"
          className={botaoBarra}
        >
          <Maximize2 size={11} />
        </button>
      </div>
    )
  }

  return (
    <aside
      ref={(el) => {
        janelaRef.current = el
      }}
      aria-label={titulo}
      data-testid={`${prefixo}-janela`}
      data-minimizada="false"
      style={{ ...posicao, width: tamanho.w, height: tamanho.h, zIndex: z }}
      onPointerDownCapture={trazer}
      className="pointer-events-auto absolute flex flex-col rounded-md border border-[var(--color-border)] bg-[var(--color-panel)] text-xs shadow-2xl"
    >
      <header
        data-testid={`${prefixo}-titulo`}
        onPointerDown={(e) => comecarArrasto(e)}
        title="Arrasta para mudar o painel de sítio"
        className="flex shrink-0 cursor-move select-none items-center gap-2 border-b border-[var(--color-border)] px-3 py-2"
      >
        {icone}
        <strong className="shrink-0">{titulo}</strong>
        {naBarra}
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {aoDestacar && (
            <BotaoDestacar onClick={aoDestacar} rotulo={`Destacar ${oQue} para uma janela do browser`} />
          )}
          <button
            type="button"
            onClick={() => setMinimizado(true)}
            aria-label={`Minimizar ${oQue}`}
            title={dicaMinimizar ? `Minimizar (${dicaMinimizar})` : 'Minimizar'}
            className={botaoBarra}
          >
            <Minus size={12} />
          </button>
          <button type="button" onClick={aoFechar} aria-label={`Fechar ${oQue}`} title="Fechar" className={botaoBarra}>
            <X size={14} />
          </button>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

      <div
        data-testid={`${prefixo}-redimensionar`}
        onPointerDown={comecarTamanho}
        title="Arrasta para mudar o tamanho"
        aria-hidden="true"
        className="absolute bottom-0 right-0 h-3 w-3 cursor-nwse-resize"
        style={{
          background:
            'linear-gradient(135deg, transparent 50%, var(--color-border) 50%, var(--color-border) 60%, transparent 60%, transparent 75%, var(--color-border) 75%, var(--color-border) 85%, transparent 85%)',
        }}
      />
    </aside>
  )
}
